import { useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { ChangelogPreviewToolbar } from "./changelog-preview-toolbar";
import { ChangelogPreviewIframe } from "./changelog-preview-iframe";
import { ChangelogFullscreenPreview } from "./changelog-fullscreen-preview";
import type { ChangelogPreviewData } from "./changelog-preview-html-builder";
import type { ChangelogEntryFormValues } from "@/lib/validation/changelog-schema";
import type { ChangelogCategory } from "@/types/changelog";

interface ChangelogPreviewPanelProps {
  categories: ChangelogCategory[];
  lang?: "ko" | "en";
}

export function ChangelogPreviewPanel({
  categories,
  lang = "ko",
}: ChangelogPreviewPanelProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { watch } = useFormContext<ChangelogEntryFormValues>();

  const titleKo = watch("titleKo");
  const titleEn = watch("titleEn");
  const bodyKo = watch("bodyKo");
  const bodyEn = watch("bodyEn");
  const featuredImageUrl = watch("featuredImageUrl");
  const categoryIds = watch("categoryIds");

  const previewData = useMemo<ChangelogPreviewData>(() => {
    const title = lang === "ko" ? titleKo : (titleEn || "");
    const body = lang === "ko" ? bodyKo : (bodyEn || "");

    const selectedCategories = categories
      .filter((cat) => categoryIds.includes(cat._id))
      .map((cat) => ({
        name: lang === "ko" ? cat.name.ko : (cat.name.en ?? cat.name.ko),
        color: cat.color,
      }));

    return {
      title,
      body,
      featuredImageUrl: featuredImageUrl || undefined,
      categories: selectedCategories,
    };
  }, [titleKo, titleEn, bodyKo, bodyEn, featuredImageUrl, categoryIds, categories, lang]);

  return (
    <div className="flex h-full flex-col">
      <ChangelogPreviewToolbar
        onToggleFullscreen={() => setIsFullscreen(true)}
      />

      <div className="flex-1 overflow-hidden">
        <ChangelogPreviewIframe previewData={previewData} />
      </div>

      {isFullscreen && (
        <ChangelogFullscreenPreview
          previewData={previewData}
          onClose={() => setIsFullscreen(false)}
        />
      )}
    </div>
  );
}
